import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Mic, MicOff, Video, VideoOff, Monitor, MonitorOff,
  PhoneOff, Maximize2, Minimize2, MessageSquare, Circle, Pencil, Volume2, RotateCw,
} from 'lucide-react';
import { ActiveCall, CallParticipant, Message, User, Attachment, Channel } from '../types';
import { callService } from '../services/callService';
import { recordingService } from '../services/recordingService';
import { dbService } from '../services/dbService';
import { socketService } from '../services/socketService';
import { callController } from '../services/callController';
import { ChatWindow } from './ChatWindow';
import Whiteboard from './Whiteboard';

interface VideoCallWindowProps {
  call: ActiveCall;
  currentUserId: string;
  onHangup: () => void;
  statusMessage?: string | null;
  // 채팅 패널용 props
  messages?: Message[];
  currentUser?: User;
  channel?: Channel;
  onSendMessage?: (text: string, attachments?: Attachment[], replyToId?: number | null) => Promise<boolean> | void;
  hasMore?: boolean;
  onLoadMore?: () => void;
  // 날짜 점프 상태를 App과 공유한다 (messages 배열이 같아서 과거 창이면 여기도 과거를 보게 된다)
  hasMoreNewer?: boolean;
  onLoadNewer?: () => void;
  onReturnToLatest?: () => void;
  /** 연결하지 못한 참가자를 다시 잇는다(타일의 '다시 연결') */
  onRetryParticipant?: (userId: string) => void;
}

const VideoCallWindow: React.FC<VideoCallWindowProps> = ({
  call, currentUserId, onHangup, statusMessage,
  messages, currentUser, channel, onSendMessage, hasMore, onLoadMore,
  hasMoreNewer, onLoadNewer, onReturnToLatest, onRetryParticipant,
}) => {
  // 통화에 들어올 때 켠 장치 — 없는 장치의 버튼은 비활성이다(App이 prepareLocalMedia를 마친 뒤 마운트된다)
  const [mediaInfo] = useState(() => callService.getLocalMediaInfo());
  const viewOnly = !mediaInfo.hasMic && !mediaInfo.hasCamera;
  const [isMuted, setIsMuted] = useState(!mediaInfo.hasMic);
  const [isCameraOff, setIsCameraOff] = useState(!mediaInfo.hasCamera);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingElapsed, setRecordingElapsed] = useState(0);
  // 참가 시점에 이미 녹화 중이던 사람·열려 있던 화이트보드는 참가 ack로 온다(늦게 들어온 사람도 '○○ 녹화 중'을 본다)
  const [remoteRecorders, setRemoteRecorders] = useState<{ userId: string; userName: string }[]>(
    () => (call.initialRecorders || []).filter(r => r.userId !== currentUserId),
  );
  const [isWhiteboardOpen, setIsWhiteboardOpen] = useState(() => !!call.initialWhiteboard?.open);
  // 크게 보기: null = 화면 공유하는 사람이 있으면 그 사람, 'grid' = 크게 보기 끔, 그 밖 = 고른 사람
  const [pin, setPin] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const prevMessageCountRef = useRef(messages?.length ?? 0);

  // 채팅 패널 사용 가능 여부
  const canChat = !!(messages && currentUser && channel && onSendMessage);

  // 채팅 패널이 닫혀 있을 때 새 메시지 수 추적
  useEffect(() => {
    if (!messages) return;
    const currentCount = messages.length;
    if (isChatOpen) {
      // 채팅 열려 있으면 읽음 처리
      setUnreadCount(0);
      prevMessageCountRef.current = currentCount;
    } else {
      // 채팅 닫혀 있으면 새 메시지 수 증가
      const diff = currentCount - prevMessageCountRef.current;
      if (diff > 0) {
        setUnreadCount(prev => prev + diff);
        prevMessageCountRef.current = currentCount;
      }
    }
  }, [messages?.length, isChatOpen]);

  // 참가 ack는 통화 창이 뜬 뒤에 도착한다 — 그때 온 녹화자·화이트보드를 반영한다
  useEffect(() => {
    const initial = call.initialRecorders;
    if (!initial || initial.length === 0) return;
    setRemoteRecorders(prev => {
      const next = [...prev];
      initial.forEach(r => {
        if (r.userId !== currentUserId && !next.some(x => x.userId === r.userId)) next.push(r);
      });
      return next;
    });
  }, [call.initialRecorders, currentUserId]);

  useEffect(() => {
    if (call.initialWhiteboard?.open) setIsWhiteboardOpen(true);
  }, [call.initialWhiteboard]);

  // 브라우저의 '공유 중지' 막대로 끝나도 버튼 상태를 되돌린다(예전엔 '공유 중지'로 남았다)
  useEffect(() => callController.onScreenShareEnded(() => setIsScreenSharing(false)), []);

  // 화이트보드 열기/닫기 소켓 이벤트 수신
  useEffect(() => {
    const socket = socketService.getSocket();
    if (!socket) return;

    const handleWhiteboardOpen = (data: { callId: string; userName: string }) => {
      if (data.callId !== call.callId) return;
      setIsWhiteboardOpen(true);
    };

    socket.on('whiteboard:open', handleWhiteboardOpen);
    return () => {
      socket.off('whiteboard:open', handleWhiteboardOpen);
    };
  }, [call.callId]);

  // 다른 참가자의 녹화 상태 수신
  useEffect(() => {
    const socket = socketService.getSocket();
    if (!socket) return;

    const handleRecordingChanged = (data: { callId: string; userId: string; userName: string; isRecording: boolean }) => {
      if (data.callId !== call.callId || data.userId === currentUserId) return;
      setRemoteRecorders(prev => {
        if (data.isRecording) {
          if (prev.some(r => r.userId === data.userId)) return prev;
          return [...prev, { userId: data.userId, userName: data.userName }];
        } else {
          return prev.filter(r => r.userId !== data.userId);
        }
      });
    };

    socket.on('call:recording-changed', handleRecordingChanged);
    return () => {
      socket.off('call:recording-changed', handleRecordingChanged);
    };
  }, [call.callId, currentUserId]);

  // 화이트보드 토글 (소켓으로 공유)
  const handleToggleWhiteboard = useCallback(() => {
    const socket = socketService.getSocket();
    const userName = currentUser?.name || '';
    setIsWhiteboardOpen(prev => {
      const next = !prev;
      if (next) {
        socket?.emit('whiteboard:open', { callId: call.callId, userName });
      }
      return next;
    });
  }, [call.callId, currentUser]);

  // 화면공유 지원 여부 (모바일 브라우저, Safari 등 미지원). HTTP 주소에서는 mediaDevices 자체가 없다(보기 전용으로 들어온다)
  const supportsScreenShare = !!(navigator.mediaDevices as any)?.getDisplayMedia && mediaInfo.canScreenShare;

  const localVideoRef = useRef<HTMLVideoElement>(null);
  // 리모트 비디오 ref 맵 (녹화용)
  const remoteVideoRefs = useRef<Map<string, HTMLVideoElement>>(new Map());
  // 리모트 소리 — 영상과 분리해 숨은 audio 요소로만 재생한다(최소화해도 끊기지 않게)
  const remoteAudioRefs = useRef<Map<string, HTMLAudioElement>>(new Map());
  const [audioBlocked, setAudioBlocked] = useState(false);

  const handleAudioBlocked = useCallback(() => setAudioBlocked(true), []);
  const setRemoteAudioRef = useCallback((userId: string, el: HTMLAudioElement | null) => {
    if (el) remoteAudioRefs.current.set(userId, el);
    else remoteAudioRefs.current.delete(userId);
  }, []);

  // 자동 재생이 막혔을 때(iOS 보기 전용 등) — 클릭 핸들러 안에서 동기로 play()를 불러야 사용자 제스처로 인정된다
  const handleResumeAudio = () => {
    setAudioBlocked(false);
    remoteAudioRefs.current.forEach(el => {
      el.play().catch(err => {
        if (err?.name === 'NotAllowedError') setAudioBlocked(true);
      });
    });
  };

  // 경과 시간 타이머
  useEffect(() => {
    const timer = setInterval(() => {
      const secs = Math.floor((Date.now() - call.startedAt.getTime()) / 1000);
      setElapsed(secs);
    }, 1000);
    return () => clearInterval(timer);
  }, [call.startedAt]);

  // 녹화 경과 시간 타이머
  useEffect(() => {
    if (!isRecording) {
      setRecordingElapsed(0);
      return;
    }
    const timer = setInterval(() => {
      setRecordingElapsed(recordingService.getElapsedTime());
    }, 1000);
    return () => clearInterval(timer);
  }, [isRecording]);

  // 로컬 스트림 → video 엘리먼트 연결
  // isCameraOff/isMinimized 변경 시 video 엘리먼트가 재생성되므로 다시 연결 필요
  useEffect(() => {
    const stream = callService.getLocalStreamInstance();
    if (localVideoRef.current && stream) {
      localVideoRef.current.srcObject = stream;
    }
  }, [isCameraOff, isMinimized]);

  const formatTime = (secs: number) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const handleToggleMute = () => {
    const nowMuted = callService.toggleMute();
    setIsMuted(nowMuted);
  };

  const handleToggleCamera = () => {
    const nowOff = callService.toggleCamera();
    setIsCameraOff(nowOff);
  };

  const handleToggleScreen = async () => {
    if (isScreenSharing) {
      callService.stopScreenShare();
      setIsScreenSharing(false);
    } else {
      const stream = await callService.startScreenShare();
      if (stream) setIsScreenSharing(true);
    }
  };

  // 녹화 시작/중지
  const handleToggleRecording = useCallback(async () => {
    if (isRecording) {
      // 녹화 중지 → 업로드
      const blob = await recordingService.stopRecording();
      setIsRecording(false);
      socketService.notifyRecordingChanged(call.callId, false);
      if (blob && onSendMessage && channel) {
        const now = new Date();
        const ts = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
        const file = new File([blob], `recording_${ts}.webm`, { type: blob.type });
        try {
          const attachments = await dbService.uploadFiles([file]);
          onSendMessage('녹화 영상', attachments);
        } catch (err) {
          console.error('[VideoCallWindow] 녹화 업로드 실패:', err);
        }
      }
    } else {
      // 녹화 시작 — 보통은 대체 트랙 덕분에 로컬 스트림이 있다. 그것마저 못 만들었으면 빈 스트림으로 상대만 녹화한다
      const localStream = callService.getLocalStreamInstance() ?? new MediaStream();

      const remoteStreams = call.participants
        .filter(p => p.userId !== currentUserId && p.stream)
        .map(p => p.stream!);

      // 비디오는 매 프레임 지금 마운트된 요소를 읽는다 — 녹화 중에 들어온 사람·크게 보기로 바뀐 타일도 담긴다
      const started = recordingService.startRecording(
        localStream, remoteStreams, () => Array.from(remoteVideoRefs.current.values()), () => localVideoRef.current,
      );
      setIsRecording(started);
      if (started) {
        socketService.notifyRecordingChanged(call.callId, true);
      }
    }
  }, [isRecording, call.callId, call.participants, currentUserId, onSendMessage, channel]);

  // 원격 참가자 (자신 제외)
  const remoteParticipants = call.participants.filter(p => p.userId !== currentUserId);

  // 녹화 중에 들어온 사람의 소리도 녹화에 더한다(같은 스트림은 한 번만 — recordingService가 거른다)
  useEffect(() => {
    if (!isRecording) return;
    remoteParticipants.forEach(p => { if (p.stream) recordingService.addAudioStream(p.stream); });
  }, [isRecording, remoteParticipants]);

  // 크게 보기 대상 — 고른 사람 → 화면 공유하는 사람 순. 2명 이상일 때만 의미가 있다
  const sharer = remoteParticipants.find(p => p.isSharingScreen && p.stream);
  const spotlightId = remoteParticipants.length < 2 || pin === 'grid'
    ? null
    : (pin && remoteParticipants.some(p => p.userId === pin) ? pin : sharer?.userId ?? null);
  const spotlight = spotlightId ? remoteParticipants.find(p => p.userId === spotlightId) : undefined;

  // 리모트 비디오 ref 등록 콜백
  const setRemoteVideoRef = useCallback((userId: string, el: HTMLVideoElement | null) => {
    if (el) {
      remoteVideoRefs.current.set(userId, el);
    } else {
      remoteVideoRefs.current.delete(userId);
    }
  }, []);

  // 소리는 최소화 여부와 무관하게 같은 자리(fragment 첫 자식)에 두어 다시 마운트되지 않게 한다
  const remoteAudio = (
    <div className="hidden" aria-hidden="true">
      {remoteParticipants.map(p => p.stream && (
        <RemoteAudio
          key={p.userId}
          userId={p.userId}
          stream={p.stream}
          onElement={setRemoteAudioRef}
          onBlocked={handleAudioBlocked}
        />
      ))}
    </div>
  );

  if (isMinimized) {
    return (
      <>
      {remoteAudio}
      <div className="fixed bottom-6 right-6 z-50 bg-gray-900 rounded-2xl shadow-2xl border border-gray-700 p-3 flex items-center gap-3 cursor-pointer"
        onClick={() => setIsMinimized(false)}>
        <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center">
          <Video size={14} className="text-white" />
        </div>
        <div>
          <p className="text-white text-xs font-medium">{call.channelName}</p>
          <p className="text-gray-400 text-xs">{formatTime(elapsed)} • {call.participants.length}명</p>
        </div>
        {isRecording && (
          <div className="w-3 h-3 rounded-full bg-brand-500 animate-pulse" title="녹화 중" />
        )}
        <button
          onClick={(e) => { e.stopPropagation(); onHangup(); }}
          className="w-8 h-8 rounded-full bg-brand-600 hover:bg-brand-700 flex items-center justify-center ml-2 transition-colors"
        >
          <PhoneOff size={13} className="text-white" />
        </button>
      </div>
      </>
    );
  }

  return (
    <>
    {remoteAudio}
    {/* PC앱 제목 표시줄 아래에서 시작 — 덮으면 통화 중에 창을 옮기거나 최소화할 수 없다(TitleBar의 --titlebar-h 주석) */}
    <div className="fixed inset-x-0 bottom-0 top-[var(--titlebar-h,0px)] z-40 bg-gray-950 flex flex-col">
      {/* 상단 헤더 — 좁은 창에서 방 이름이 길면 줄이고 최소화 버튼은 밀려나지 않는다 */}
      <div className="flex items-center justify-between gap-3 px-4 md:px-6 py-3 bg-gray-900 border-b border-gray-800">
        <div className="flex items-center gap-3 min-w-0 flex-1 overflow-hidden">
          <span className="text-white font-semibold truncate">{call.channelName}</span>
          <span className="text-gray-400 text-sm shrink-0">통화 중 • {formatTime(elapsed)} • {call.participants.length}명</span>
          {viewOnly && (
            <span
              className="shrink-0 px-2 py-0.5 rounded-full bg-gray-700 text-gray-200 text-xs font-medium"
              title="카메라·마이크 없이 상대 화면과 소리만 받습니다"
            >
              보기 전용
            </span>
          )}
          {isRecording && (
            <div className="flex items-center gap-1.5 px-2 py-0.5 bg-brand-900/50 rounded-full">
              <div className="w-2 h-2 rounded-full bg-brand-500 animate-pulse" />
              <span className="text-brand-400 text-xs font-medium">REC {formatTime(recordingElapsed)}</span>
            </div>
          )}
          {remoteRecorders.map(r => (
            <div key={r.userId} className="flex items-center gap-1.5 px-2 py-0.5 bg-brand-900/50 rounded-full">
              <div className="w-2 h-2 rounded-full bg-brand-500 animate-pulse" />
              <span className="text-brand-400 text-xs font-medium">{r.userName} 녹화 중</span>
            </div>
          ))}
        </div>
        <button
          onClick={() => setIsMinimized(true)}
          className="shrink-0 text-gray-400 hover:text-white transition-colors"
          title="최소화"
        >
          <Minimize2 size={18} />
        </button>
      </div>

      {/* 비디오 + 채팅 영역 */}
      <div className="flex-1 flex overflow-hidden relative">
        {/* 비디오 그리드 */}
        <div className="flex-1 relative overflow-hidden p-4">
          {remoteParticipants.length === 0 ? (
            // 상대방 없을 때 대기 화면
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-gray-700 flex items-center justify-center mx-auto mb-4">
                  <Video size={32} className={statusMessage ? 'text-brand-400' : 'text-gray-400'} />
                </div>
                <p className={statusMessage ? 'text-brand-400 font-medium' : 'text-gray-400'}>
                  {statusMessage ?? '상대방을 기다리는 중...'}
                </p>
              </div>
            </div>
          ) : spotlight ? (
            // 크게 보기 — 화면 공유하는 사람(또는 고른 사람)을 크게, 나머지는 아래 줄에. 큰 타일을 누르면 격자로 돌아간다
            <div className="h-full min-h-0 flex flex-col gap-3">
              <div className="flex-1 min-h-0">
                <RemoteVideoTile
                  key={spotlight.userId}
                  participant={spotlight}
                  onVideoRef={(el) => setRemoteVideoRef(spotlight.userId, el)}
                  onClick={() => setPin('grid')}
                  clickLabel="크게 보기 끄기"
                  onRetry={onRetryParticipant}
                />
              </div>
              <div className="h-24 sm:h-28 shrink-0 flex gap-3 overflow-x-auto pr-28 sm:pr-44">
                {remoteParticipants.filter(p => p.userId !== spotlight.userId).map(participant => (
                  <div key={participant.userId} className="h-full aspect-video shrink-0">
                    <RemoteVideoTile
                      participant={participant}
                      onVideoRef={(el) => setRemoteVideoRef(participant.userId, el)}
                      onClick={() => setPin(participant.userId)}
                      clickLabel="크게 보기"
                      onRetry={onRetryParticipant}
                      compact
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={`h-full min-h-0 grid gap-3 ${
              remoteParticipants.length === 1 ? 'grid-cols-1' :
              remoteParticipants.length <= 4 ? 'grid-cols-2' :
              'grid-cols-3'
            }`}>
              {remoteParticipants.map(participant => (
                <RemoteVideoTile
                  key={participant.userId}
                  participant={participant}
                  onVideoRef={(el) => setRemoteVideoRef(participant.userId, el)}
                  onClick={remoteParticipants.length >= 2 ? () => setPin(participant.userId) : undefined}
                  clickLabel="크게 보기"
                  onRetry={onRetryParticipant}
                />
              ))}
            </div>
          )}

          {/* 자동 재생이 막혀 상대 소리가 나오지 않을 때 */}
          {audioBlocked && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
              <button
                onClick={handleResumeAudio}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white text-gray-900 text-sm font-medium shadow-lg hover:bg-slate-100 transition-colors"
              >
                <Volume2 size={16} />
                소리 켜기
              </button>
            </div>
          )}

          {/* 화이트보드 오버레이 */}
          {isWhiteboardOpen && (
            <div className="absolute inset-0 z-10">
              <Whiteboard
                callId={call.callId}
                onClose={() => setIsWhiteboardOpen(false)}
                initialHistory={call.initialWhiteboard?.actions}
              />
            </div>
          )}

          {/* 로컬 PiP */}
          <div className={`absolute right-3 sm:right-6 w-24 h-16 sm:w-36 sm:h-24 rounded-xl overflow-hidden border-2 border-gray-600 bg-gray-800 shadow-lg z-20 ${spotlight ? 'bottom-4' : 'bottom-3'}`}>
            {isCameraOff ? (
              <div className="w-full h-full flex items-center justify-center bg-gray-800">
                <VideoOff size={20} className="text-gray-500" />
              </div>
            ) : (
              <video
                ref={localVideoRef}
                autoPlay
                muted
                playsInline
                className="w-full h-full object-cover scale-x-[-1]"
              />
            )}
            <div className="absolute bottom-1 left-2 text-xs text-white opacity-80">나</div>
          </div>
        </div>

        {/* 채팅 사이드 패널 — 좁은 화면(휴대폰·좁힌 PC앱)에서는 옆에 두면 영상이 수십 px로 짜부라지므로
            영상 위를 덮는다. 닫기는 아래 컨트롤바의 채팅 토글 */}
        {isChatOpen && canChat && (
          <div className="absolute inset-0 z-30 md:static md:z-auto md:w-80 lg:w-96 md:border-l border-gray-800 flex flex-col bg-gray-950">
            <ChatWindow
              messages={messages!}
              currentUser={currentUser!}
              channel={channel!}
              onSendMessage={onSendMessage!}
              hasMore={hasMore}
              onLoadMore={onLoadMore}
              hasMoreNewer={hasMoreNewer}
              onLoadNewer={onLoadNewer}
              onReturnToLatest={onReturnToLatest}
              darkMode
            />
          </div>
        )}
      </div>

      {/* 컨트롤 바 */}
      <div className="bg-gray-900 border-t border-gray-800 py-3 sm:py-4 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
        <ControlButton
          active={isMuted}
          activeIcon={<MicOff size={20} />}
          inactiveIcon={<Mic size={20} />}
          activeLabel="음소거 해제"
          inactiveLabel="음소거"
          activeClass="bg-brand-600 hover:bg-brand-700"
          inactiveClass="bg-gray-700 hover:bg-gray-600"
          onClick={handleToggleMute}
          disabled={!mediaInfo.hasMic}
          disabledLabel="마이크 없음"
        />
        <ControlButton
          active={isCameraOff}
          activeIcon={<VideoOff size={20} />}
          inactiveIcon={<Video size={20} />}
          activeLabel="카메라 켜기"
          inactiveLabel="카메라 끄기"
          activeClass="bg-brand-600 hover:bg-brand-700"
          inactiveClass="bg-gray-700 hover:bg-gray-600"
          onClick={handleToggleCamera}
          disabled={!mediaInfo.hasCamera}
          disabledLabel="카메라 없음"
        />
        {supportsScreenShare && (
          <ControlButton
            active={isScreenSharing}
            activeIcon={<MonitorOff size={20} />}
            inactiveIcon={<Monitor size={20} />}
            activeLabel="공유 중지"
            inactiveLabel="화면 공유"
            activeClass="bg-white !text-chrome hover:bg-slate-200"
            inactiveClass="bg-gray-700 hover:bg-gray-600"
            onClick={handleToggleScreen}
          />
        )}
        <ControlButton
          active={isRecording}
          activeIcon={<Circle size={20} fill="currentColor" />}
          inactiveIcon={<Circle size={20} />}
          activeLabel="녹화 중지"
          inactiveLabel="녹화"
          activeClass="bg-brand-600 hover:bg-brand-700"
          inactiveClass="bg-gray-700 hover:bg-gray-600"
          onClick={handleToggleRecording}
        />
        <ControlButton
          active={isWhiteboardOpen}
          activeIcon={<Pencil size={20} />}
          inactiveIcon={<Pencil size={20} />}
          activeLabel="화이트보드 닫기"
          inactiveLabel="화이트보드"
          activeClass="bg-white !text-chrome hover:bg-slate-200"
          inactiveClass="bg-gray-700 hover:bg-gray-600"
          onClick={handleToggleWhiteboard}
        />
        {canChat && (
          <ControlButton
            active={isChatOpen}
            activeIcon={<MessageSquare size={20} />}
            inactiveIcon={<MessageSquare size={20} />}
            activeLabel="채팅 닫기"
            inactiveLabel="채팅"
            activeClass="bg-white !text-chrome hover:bg-slate-200"
            inactiveClass="bg-gray-700 hover:bg-gray-600"
            onClick={() => setIsChatOpen(prev => !prev)}
            badge={isChatOpen ? 0 : unreadCount}
          />
        )}
        <button
          onClick={onHangup}
          className="w-14 h-14 rounded-full bg-brand-600 hover:bg-brand-700 flex items-center justify-center transition-colors shadow-lg"
          title="통화 종료"
        >
          <PhoneOff size={22} className="text-white" />
        </button>
      </div>
    </div>
    </>
  );
};

// 원격 참가자 소리 — 숨은 audio 요소. 원격 <video>는 muted라 소리는 여기서만 난다(이중 재생 방지)
const RemoteAudio: React.FC<{
  userId: string;
  stream: MediaStream;
  onElement: (userId: string, el: HTMLAudioElement | null) => void;
  onBlocked: () => void;
}> = ({ userId, stream, onElement, onBlocked }) => {
  const ref = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.srcObject = stream;
    el.play().catch(err => {
      // 자동 재생 정책에 막혔을 때만 '소리 켜기'를 띄운다(AbortError는 srcObject 교체로 생기는 정상 중단)
      if (err?.name === 'NotAllowedError') onBlocked();
    });
  }, [stream, onBlocked]);

  useEffect(() => {
    onElement(userId, ref.current);
    return () => onElement(userId, null);
  }, [userId, onElement]);

  return <audio ref={ref} autoPlay />;
};

// 원격 참가자 비디오 타일
const RemoteVideoTile: React.FC<{
  participant: CallParticipant;
  onVideoRef?: (el: HTMLVideoElement | null) => void;
  /** 누르면 크게 보기를 켜거나 끈다 */
  onClick?: () => void;
  clickLabel?: string;
  onRetry?: (userId: string) => void;
  /** 크게 보기 아래 줄의 작은 타일 */
  compact?: boolean;
}> = ({ participant, onVideoRef, onClick, clickLabel, onRetry, compact }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video && participant.stream) {
      video.srcObject = participant.stream;
      // autoPlay가 브라우저 정책으로 차단될 수 있으므로 명시적 play
      video.play().catch(() => {});
    }
  }, [participant.stream]);

  // 비디오 ref를 부모에게 전달 (녹화용)
  useEffect(() => {
    onVideoRef?.(videoRef.current);
    return () => onVideoRef?.(null);
  }, [onVideoRef]);

  // 상대가 카메라를 껐거나 카메라가 없으면(데이터 채널로 알려 온 상태) 이름 동그라미. 구버전 앱은 알려 주지 않아 undefined → 영상
  const showVideo = !!participant.stream && participant.isCameraOff !== true;
  // 화면 공유는 잘리면 안 된다(글자·가장자리) — contain. 카메라는 타일을 채운다
  const fit = participant.isSharingScreen ? 'object-contain bg-black' : 'object-cover';
  const conn = participant.connState;
  const connLabel = conn === 'connecting' && !participant.stream ? '연결 중…'
    : conn === 'reconnecting' ? '다시 연결 중…'
    : conn === 'failed' ? '연결하지 못했습니다'
    : null;

  return (
    <div
      className={`relative bg-gray-800 rounded-xl overflow-hidden flex items-center justify-center h-full min-h-0 ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      title={onClick ? clickLabel : undefined}
    >
      {/* video는 계속 마운트해 둔다 — srcObject·녹화용 ref 유지. 소리는 RemoteAudio가 내므로 muted */}
      {participant.stream && (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`w-full h-full ${fit} ${showVideo ? '' : 'invisible'}`}
        />
      )}
      {!showVideo && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-gray-800">
          <div className={`${compact ? 'w-9 h-9 text-base' : 'w-16 h-16 text-2xl'} rounded-full bg-gray-700 flex items-center justify-center font-bold text-gray-300`}>
            {participant.userName.charAt(0)}
          </div>
          {!compact && <p className="text-gray-400 text-sm">{participant.userName}</p>}
        </div>
      )}

      {/* 연결 상태 — 끊겨도 타일은 남고 다시 잇는다 */}
      {connLabel && (
        <div className="absolute inset-x-0 top-2 flex justify-center pointer-events-none">
          <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 text-gray-100 text-xs pointer-events-auto">
            {connLabel}
            {conn === 'failed' && onRetry && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onRetry(participant.userId); }}
                className="flex items-center gap-1 text-white font-medium hover:underline"
              >
                <RotateCw size={12} /> 다시 연결
              </button>
            )}
          </span>
        </div>
      )}

      {/* 이름 오버레이 */}
      <div className="absolute bottom-2 left-3 flex items-center gap-1.5 min-w-0 max-w-[calc(100%-1.5rem)]">
        <span className={`text-white font-medium drop-shadow truncate ${compact ? 'text-xs' : 'text-sm'}`}>{participant.userName}</span>
        {participant.isSharingScreen && !compact && (
          <span className="shrink-0 px-1.5 py-0.5 rounded bg-black/50 text-gray-100 text-[11px]">화면 공유</span>
        )}
        {participant.isMuted && <MicOff size={12} className="shrink-0 text-brand-400" />}
      </div>
    </div>
  );
};

// 컨트롤 버튼
interface ControlButtonProps {
  active: boolean;
  activeIcon: React.ReactNode;
  inactiveIcon: React.ReactNode;
  activeLabel: string;
  inactiveLabel: string;
  activeClass: string;
  inactiveClass: string;
  onClick: () => void;
  badge?: number;
  /** 장치가 없어 쓸 수 없는 버튼 — 꺼진 아이콘 + disabledLabel */
  disabled?: boolean;
  disabledLabel?: string;
}

const ControlButton: React.FC<ControlButtonProps> = ({
  active, activeIcon, inactiveIcon, activeLabel, inactiveLabel,
  activeClass, inactiveClass, onClick, badge, disabled, disabledLabel,
}) => {
  const label = disabled ? (disabledLabel ?? activeLabel) : active ? activeLabel : inactiveLabel;
  return (
  <div className="flex flex-col items-center gap-1">
    <div className="relative">
      <button
        onClick={onClick}
        disabled={disabled}
        className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
          disabled
            ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
            : `text-white ${active ? activeClass : inactiveClass}`
        }`}
        title={label}
      >
        {disabled || active ? activeIcon : inactiveIcon}
      </button>
      {badge != null && badge > 0 && (
        <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 flex items-center justify-center bg-brand-600 text-white text-xs font-bold rounded-full">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </div>
    <span className="text-gray-500 text-xs">{label}</span>
  </div>
  );
};

export default VideoCallWindow;
